<template>
  <!-- T1's transition between Confirm and the submitted page: the "My Privilege to
       Build" video over the campaign navy, the tag and "You are a part of history!"
       rising over its last two seconds, then Continue, which fades it all away to the
       page underneath. The navy is up from the first frame, so it meets the veil
       Confirm faded to before it swapped pages.
       If the video cannot start, its last frame stands in and the words follow at
       once, so a slow or failed load never strands the member on a blank screen. -->
  <!-- A modal dialog that takes focus, so VoiceOver and a keyboard stay on it: the
       page behind is also aria-hidden, as `inert` alone does nothing before iOS 15.5. -->
  <section
    ref="dialog"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    class="fixed outline-none inset-0 z-[100] overflow-hidden bg-campaign-primary transition-opacity duration-[900ms] ease-in-out"
    :class="leaving && fadeOut ? 'opacity-0' : 'opacity-100'"
    aria-labelledby="bf-submitted-video-heading"
    data-test="bf-submitted-video"
    @transitionend.self="finish"
  >
    <video
      ref="video"
      :src="videoUrl"
      class="absolute inset-0 size-full object-cover transition-opacity duration-[1100ms]"
      :class="stage === 'playing' ? 'opacity-100' : 'opacity-0'"
      autoplay
      muted
      playsinline
      preload="auto"
      aria-hidden="true"
      @playing="onPlaying"
      @timeupdate="onTimeUpdate"
      @ended="revealAll"
      @error="showStill"
    />
    <img
      v-if="stage === 'still'"
      :src="stillUrl"
      alt=""
      class="absolute inset-0 size-full object-cover animate-bf-fade-in motion-reduce:animate-none"
      data-test="bf-submitted-still"
    />
    <div
      class="absolute inset-x-0 bottom-0 h-[300px] bg-gradient-to-b from-transparent to-campaign-primary to-[70%] opacity-90"
      aria-hidden="true"
    />
    <div
      v-if="wordsShown"
      class="absolute inset-x-[24px] bottom-[calc(132px+env(safe-area-inset-bottom))] flex flex-col items-center gap-[12px] text-center"
    >
      <DsTag variant="campaign-outline" class="animate-bf-rise motion-reduce:animate-none"
        >BF2026 · MY PRIVILEGE TO BUILD</DsTag
      >
      <DsHeading
        id="bf-submitted-video-heading"
        tag="h1"
        class="font-anton font-normal uppercase tracking-[0.01em] text-[42px] leading-[46px] text-campaign-accent animate-bf-rise [animation-delay:700ms] motion-reduce:animate-none"
      >
        You are a part<br />of history!
      </DsHeading>
      <!-- A FAST gift's amount and ID, as the hands animation other gifts get shows
           them. A pledge has neither. -->
      <div
        v-if="giftAmount"
        class="flex flex-col gap-[6px] text-campaign-on-primary animate-bf-rise [animation-delay:1100ms] motion-reduce:animate-none"
        data-test="bf-submitted-gift"
      >
        <p class="m-0 text-[26px] leading-[32px] font-bold">You gave S${{ giftAmount }}</p>
        <p v-if="transactionId" class="m-0 text-[18px] leading-[24px] font-semibold opacity-90">
          ID: {{ transactionId }}
        </p>
      </div>
    </div>
    <!-- Rendered only once it is due, not merely transparent until then, so a stray
         tap or a tab cannot skip the video before Continue is there. -->
    <button
      v-if="ctaShown"
      type="button"
      class="absolute inset-x-[24px] bottom-[calc(44px+env(safe-area-inset-bottom))] h-[54px] rounded-full border-[1.5px] border-white/50 bg-white/[0.14] backdrop-blur-[8px] text-[16px] font-bold tracking-[0.02em] text-campaign-on-primary animate-bf-rise motion-reduce:animate-none"
      :disabled="leaving"
      data-test="bf-submitted-continue"
      @click="leave"
    >
      Continue
    </button>
  </section>
</template>

<script>
import { Heading as DsHeading, Tag as DsTag } from '@team-hogc/hogc-one-design-system';
import {
  BF_PLEDGE_SUBMITTED_STILL,
  BF_PLEDGE_SUBMITTED_VIDEO,
} from '@/constants/MyGiving/building-fund-pledge';

// Where in the 7.2s video the words rise and Continue arrives, as the T1 frame times
// them. Read off the video itself, not a clock, so a stall on a slow connection holds
// them back with the picture rather than showing Continue over a frozen frame.
const WORDS_AT = 5.0;
const CTA_AT = 6.8;
// Over the still, Continue follows the words after this long.
const STILL_CTA_MS = 900;
// However the video goes once started, everything is up by then (its 7.2s and a margin).
const PLAYING_TIMEOUT = 12000;
// How long the video gets to start before the still takes its place.
const START_TIMEOUT = 2500;
// The fade out (duration-[900ms]), in case `transitionend` never comes.
const LEAVE_MS = 900;

export default {
  name: 'PledgeSubmittedVideo',

  components: { DsHeading, DsTag },

  props: {
    // A Building Fund FAST gift's, for the lines under the words. None for a pledge.
    amount: {
      type: [Number, String],
      default: null,
    },
    transactionId: {
      type: String,
      default: null,
    },
    // Fade down to the page underneath on Continue. Off when Continue leaves the
    // page instead, as a Building Fund gift's does for My Giving.
    fadeOut: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['continue'],

  data() {
    return {
      videoUrl: BF_PLEDGE_SUBMITTED_VIDEO,
      stillUrl: BF_PLEDGE_SUBMITTED_STILL,
      // null while the video loads, then 'playing' or 'still'.
      stage: null,
      wordsShown: false,
      ctaShown: false,
      leaving: false,
      finished: false,
      timers: [],
    };
  },

  computed: {
    // As the hands animation writes it: `1,000` → `1000.00`.
    giftAmount() {
      if (this.amount === null || this.amount === undefined || this.amount === '') return '';
      const value = parseFloat(String(this.amount).replace(/,/g, ''));
      return Number.isFinite(value) ? value.toFixed(2) : '';
    },
  },

  mounted() {
    this.$refs.dialog.focus?.();
    this.timers.push(setTimeout(this.showStill, START_TIMEOUT));
    // Autoplay can be refused (Low Power Mode on iOS); `play()` says so.
    this.$refs.video.play?.()?.catch?.(this.showStill);
  },

  beforeUnmount() {
    this.timers.forEach(clearTimeout);
  },

  methods: {
    onPlaying() {
      if (this.stage) return;
      this.stage = 'playing';
      this.timers.push(setTimeout(this.revealAll, PLAYING_TIMEOUT));
    },

    onTimeUpdate() {
      if (this.stage !== 'playing') return;
      const time = this.$refs.video.currentTime;
      if (time >= WORDS_AT) this.wordsShown = true;
      if (time >= CTA_AT) this.ctaShown = true;
    },

    revealAll() {
      this.wordsShown = true;
      this.ctaShown = true;
    },

    showStill() {
      if (this.stage) return;
      this.stage = 'still';
      this.wordsShown = true;
      this.$refs.video?.pause?.();
      this.timers.push(setTimeout(this.revealAll, STILL_CTA_MS));
    },

    leave() {
      if (this.leaving) return;
      this.leaving = true;
      if (!this.fadeOut) {
        this.finish();
        return;
      }
      this.timers.push(setTimeout(this.finish, LEAVE_MS + 100));
    },

    finish() {
      if (!this.leaving || this.finished) return;
      this.finished = true;
      this.$emit('continue');
    },
  },
};
</script>
